// The menu-permission matrix from the user-role mock-up: Module → Section →
// Screen down the left, View / Add / Edit / Delete across the top, with a
// search box, Full / View only / None presets, expand-collapse and a granted
// counter.
//
// It is driven entirely by the rows the permission endpoints already return
// ({ menu: { id, name, parentID }, view, add, edit, deletion }) and hands the
// same rows back through onChange, so what gets saved is byte-for-byte what
// the old checkbox list saved. The four boxes stay independent, exactly as
// they are today — nothing is forced on or off behind your back.
import React, { useMemo, useState } from "react";
import { AxSearchIcon } from "../ui/adminUi";

const ACTIONS = [
  { k: "view", t: "View" },
  { k: "add", t: "Add" },
  { k: "edit", t: "Edit" },
  { k: "deletion", t: "Delete" },
];

// Split the flat rows into module → children. A row is a module when its
// parentID is 0 or points at something the response did not include.
const buildTree = (rows) => {
  const byId = {};
  rows.forEach((r) => {
    byId[r.menu.id] = r;
  });
  const childrenOf = {};
  const roots = [];
  rows.forEach((r) => {
    const pid = r.menu.parentID;
    if (pid === 0 || !byId[pid]) roots.push(r);
    else {
      if (!childrenOf[pid]) childrenOf[pid] = [];
      childrenOf[pid].push(r);
    }
  });
  return roots.map((root) => {
    const kids = childrenOf[root.menu.id] || [];
    const sections = [];
    const leaves = [];
    kids.forEach((k) => {
      if (childrenOf[k.menu.id]?.length)
        sections.push({ head: k, items: childrenOf[k.menu.id] });
      else leaves.push(k);
    });
    // a module with no children at all is a screen in its own right
    if (!kids.length) leaves.push(root);
    return { module: root, sections, leaves };
  });
};

const mark = (text, q) => {
  if (!q) return text;
  const i = text.toLowerCase().indexOf(q);
  if (i < 0) return text;
  return (
    <>
      {text.slice(0, i)}
      <mark>{text.slice(i, i + q.length)}</mark>
      {text.slice(i + q.length)}
    </>
  );
};

const TriBox = ({ on, total, ...rest }) => (
  <input
    type="checkbox"
    checked={total > 0 && on === total}
    ref={(el) => {
      if (el) el.indeterminate = on > 0 && on < total;
    }}
    {...rest}
  />
);

const PermissionMatrix = ({ rows = [], onChange, loading, emptyText }) => {
  const [q, setQ] = useState("");
  const [grantedOnly, setGrantedOnly] = useState(false);
  const [open, setOpen] = useState({});

  const tree = useMemo(() => buildTree(rows || []), [rows]);
  const needle = q.trim().toLowerCase();

  const visibleOf = (items) =>
    items.filter((it) => {
      if (needle && !it.menu.name.toLowerCase().includes(needle)) return false;
      if (grantedOnly && !(it.view || it.add || it.edit || it.deletion)) return false;
      return true;
    });

  const setMany = (ids, key, on) => {
    const set = {};
    ids.forEach((id) => {
      set[id] = true;
    });
    onChange(rows.map((r) => (set[r.menu.id] ? { ...r, [key]: on } : r)));
  };
  const presetMany = (ids, preset) => {
    const set = {};
    ids.forEach((id) => {
      set[id] = true;
    });
    onChange(
      rows.map((r) =>
        set[r.menu.id]
          ? {
              ...r,
              view: preset !== "none",
              add: preset === "full",
              edit: preset === "full",
              deletion: preset === "full",
            }
          : r
      )
    );
  };

  const aggregate = (items, key) => ({
    on: items.filter((it) => it[key]).length,
    total: items.length,
  });

  // everything currently on screen, for the header's column masters
  const allVisible = [];
  const groups = tree
    .map((g) => {
      const secs = g.sections
        .map((s) => ({ head: s.head, items: visibleOf(s.items) }))
        .filter((s) => s.items.length);
      const leaves = visibleOf(g.leaves);
      const items = secs.reduce((acc, s) => acc.concat(s.items), []).concat(leaves);
      return { ...g, secs, leaves, items };
    })
    .filter((g) => g.items.length);
  groups.forEach((g) => allVisible.push(...g.items));

  const grantedCount = rows.reduce(
    (n, r) => n + (r.view ? 1 : 0) + (r.add ? 1 : 0) + (r.edit ? 1 : 0) + (r.deletion ? 1 : 0),
    0
  );
  const totalCount = rows.length * ACTIONS.length;

  return (
    <>
      {/* ---- toolbar ---- */}
      <div className="axa-permbar">
        <div className="axa-psearch">
          <AxSearchIcon />
          <input
            type="text"
            placeholder="Find a screen…"
            autoComplete="off"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
        <button
          type="button"
          className="axa-pbtn"
          disabled={!allVisible.length}
          onClick={() => presetMany(allVisible.map((i) => i.menu.id), "full")}
        >
          Full
        </button>
        <button
          type="button"
          className="axa-pbtn"
          disabled={!allVisible.length}
          onClick={() => presetMany(allVisible.map((i) => i.menu.id), "view")}
        >
          View only
        </button>
        <button
          type="button"
          className="axa-pbtn"
          disabled={!allVisible.length}
          onClick={() => presetMany(allVisible.map((i) => i.menu.id), "none")}
        >
          None
        </button>
        <span className="axa-sepbar" />
        <button
          type="button"
          className="axa-pbtn"
          onClick={() => {
            const next = {};
            groups.forEach((g) => {
              next[g.module.menu.id] = true;
            });
            setOpen(next);
          }}
        >
          Expand all
        </button>
        <button type="button" className="axa-pbtn" onClick={() => setOpen({})}>
          Collapse
        </button>
        <button
          type="button"
          className="axa-pbtn"
          aria-pressed={grantedOnly}
          onClick={() => setGrantedOnly((v) => !v)}
        >
          Granted only
        </button>
        <span className="axa-pcount">
          <b>{grantedCount}</b> of {totalCount} granted
        </span>
      </div>

      {/* ---- column headers ---- */}
      <div className="axa-mhead">
        <span className="lb">Screen</span>
        {ACTIONS.map((a) => {
          const g = aggregate(allVisible, a.k);
          return (
            <span className="act" key={a.k}>
              <TriBox
                on={g.on}
                total={g.total}
                aria-label={"All " + a.t}
                onChange={(e) =>
                  setMany(allVisible.map((i) => i.menu.id), a.k, e.target.checked)
                }
              />
              <span>{a.t}</span>
            </span>
          );
        })}
      </div>

      {/* ---- the tree ---- */}
      <div className="axa-matrix">
        {loading ? (
          <div className="axa-emptyperm">Loading permissions…</div>
        ) : !groups.length ? (
          <div className="axa-emptyperm">
            {rows.length ? "Nothing matches that filter." : emptyText || "No menu returned."}
          </div>
        ) : (
          groups.map((g) => {
            const isOpen = !!needle || !!open[g.module.menu.id];
            const granted = g.items.reduce(
              (n, r) =>
                n + (r.view ? 1 : 0) + (r.add ? 1 : 0) + (r.edit ? 1 : 0) + (r.deletion ? 1 : 0),
              0
            );
            return (
              <div
                className={"axa-mgroup" + (isOpen ? " open" : "")}
                key={g.module.menu.id}
              >
                <div className="axa-ghead">
                  <button
                    type="button"
                    className="axa-gtoggle"
                    aria-expanded={isOpen}
                    onClick={() =>
                      setOpen((o) => ({ ...o, [g.module.menu.id]: !o[g.module.menu.id] }))
                    }
                  >
                    <span className="gn">
                      <svg
                        className="chev"
                        viewBox="0 0 12 12"
                        width="11"
                        height="11"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <path d="M4.5 2.5 8 6l-3.5 3.5" />
                      </svg>
                      <b>{mark(g.module.menu.name, needle)}</b>
                      <span className="c">
                        {granted} / {g.items.length * 4}
                      </span>
                    </span>
                  </button>
                  {ACTIONS.map((a) => {
                    const agg = aggregate(g.items, a.k);
                    return (
                      <span className="cell" key={a.k}>
                        <TriBox
                          on={agg.on}
                          total={agg.total}
                          aria-label={g.module.menu.name + " " + a.t}
                          onChange={(e) =>
                            setMany(g.items.map((i) => i.menu.id), a.k, e.target.checked)
                          }
                        />
                      </span>
                    );
                  })}
                </div>

                <div className="axa-gbody">
                  {g.secs.map((s) => (
                    <React.Fragment key={s.head.menu.id}>
                      <div className="axa-shead">
                        <span className="sn">{mark(s.head.menu.name, needle)}</span>
                        {ACTIONS.map((a) => {
                          const agg = aggregate(s.items, a.k);
                          return (
                            <span className="cell" key={a.k}>
                              <TriBox
                                on={agg.on}
                                total={agg.total}
                                aria-label={s.head.menu.name + " " + a.t}
                                onChange={(e) =>
                                  setMany(s.items.map((i) => i.menu.id), a.k, e.target.checked)
                                }
                              />
                            </span>
                          );
                        })}
                      </div>
                      {s.items.map((it) => (
                        <PermRow key={it.menu.id} item={it} needle={needle} onSet={setMany} />
                      ))}
                    </React.Fragment>
                  ))}
                  {g.leaves.map((it) => (
                    <PermRow key={it.menu.id} item={it} needle={needle} onSet={setMany} />
                  ))}
                </div>
              </div>
            );
          })
        )}
      </div>
    </>
  );
};

const PermRow = ({ item, needle, onSet }) => {
  const granted = item.view || item.add || item.edit || item.deletion;
  return (
    <div className={"axa-prow" + (granted ? " granted" : "")}>
      <span className="pn">{mark(item.menu.name, needle)}</span>
      {ACTIONS.map((a) => (
        <span className="cell" key={a.k}>
          <input
            type="checkbox"
            checked={!!item[a.k]}
            aria-label={item.menu.name + " " + a.t}
            onChange={(e) => onSet([item.menu.id], a.k, e.target.checked)}
          />
        </span>
      ))}
    </div>
  );
};

export default PermissionMatrix;
